import type { AuditLog } from "@webuild-ai/admin/models/components";

import React, { useMemo, useState } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Select,
  SelectItem,
  Button,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

interface AuditLogTabProps {
  logs: AuditLog[];
  onRefresh: (actionFilter?: string) => Promise<void>;
}

const ACTION_COLOURS: Record<string, "success" | "danger" | "warning" | "primary" | "default"> = {
  role_changed: "warning",
  workflow_access_granted: "success",
  workflow_access_revoked: "danger",
  workflow_role_changed: "primary",
};

const ACTION_LABELS: Record<string, string> = {
  role_changed: "Role Changed",
  workflow_access_granted: "Access Granted",
  workflow_access_revoked: "Access Revoked",
  workflow_role_changed: "Access Changed",
};

function formatRelativeTime(
  isoDate: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t("Just now");
  if (diffMins < 60) return t("{{count}}m ago", { count: diffMins });
  if (diffHours < 24) return t("{{count}}h ago", { count: diffHours });
  if (diffDays < 7) return t("{{count}}d ago", { count: diffDays });

  return date.toLocaleDateString();
}

export const AuditLogTab: React.FC<AuditLogTabProps> = ({ logs, onRefresh }) => {
  const { t } = useTranslation();
  const [actionFilter, setActionFilter] = useState<string>("all");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const filteredLogs = useMemo(() => {
    if (actionFilter === "all") return logs;

    return logs.filter((log) => log.action === actionFilter);
  }, [logs, actionFilter]);

  const handleRefresh = async (filter?: string) => {
    const activeFilter = filter ?? actionFilter;

    setIsRefreshing(true);
    await onRefresh(activeFilter === "all" ? undefined : activeFilter);
    setIsRefreshing(false);
  };

  return (
    <div className="space-y-4 pt-4">
      <div className="flex flex-col sm:flex-row gap-3 justify-between">
        <Select
          aria-label={t("Filter by action")}
          className="w-full sm:w-56"
          selectedKeys={[actionFilter]}
          onChange={(e) => {
            setActionFilter(e.target.value);
            handleRefresh(e.target.value);
          }}
        >
          <SelectItem key="all">{t("All Actions")}</SelectItem>
          <SelectItem key="role_changed">{t("Role Changed")}</SelectItem>
          <SelectItem key="workflow_access_granted">{t("Access Granted")}</SelectItem>
          <SelectItem key="workflow_access_revoked">{t("Access Revoked")}</SelectItem>
          <SelectItem key="workflow_role_changed">{t("Access Changed")}</SelectItem>
        </Select>
        <Button
          isLoading={isRefreshing}
          startContent={<Icon icon="solar:refresh-outline" width={18} />}
          variant="flat"
          onPress={() => handleRefresh()}
        >
          {t("Refresh")}
        </Button>
      </div>

      <Table aria-label={t("Audit log table")}>
        <TableHeader>
          <TableColumn>{t("Admin")}</TableColumn>
          <TableColumn>{t("Action")}</TableColumn>
          <TableColumn>{t("Target")}</TableColumn>
          <TableColumn>{t("Detail")}</TableColumn>
          <TableColumn>{t("When")}</TableColumn>
        </TableHeader>
        <TableBody emptyContent={t("No audit log entries")} items={filteredLogs}>
          {(log) => (
            <TableRow key={log.id}>
              <TableCell>
                <div>
                  <p className="text-sm font-medium">{log.adminUserName}</p>
                  <p className="text-xs text-default-400">{log.adminUserEmail}</p>
                </div>
              </TableCell>
              <TableCell>
                <Chip color={ACTION_COLOURS[log.action] || "default"} size="sm" variant="flat">
                  {t(ACTION_LABELS[log.action] || log.action)}
                </Chip>
              </TableCell>
              <TableCell>
                <div>
                  <p className="text-sm">{log.targetUserName}</p>
                  {log.resourceName && (
                    <p className="text-xs text-default-400">{log.resourceName}</p>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-sm text-default-600">{log.detail}</TableCell>
              <TableCell className="text-sm text-default-400">
                {log.createdAt ? formatRelativeTime(log.createdAt, t) : "—"}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
